import React, { useState, useEffect, useCallback } from 'react';
import { gradesAPI } from '../../services/api';
import { formatDate, formatCurrency } from '../../utils/helpers';
import StatCard from '../../components/common/StatCard';
import StatusBadge from '../../components/common/StatusBadge';
import Modal from '../../components/common/Modal';
import SearchBar from '../../components/common/SearchBar';
import toast from 'react-hot-toast';

/* ── colour maps ──────────────────────────────────────────────────── */
const EXAM_STA = { scheduled:{bg:'#eff6ff',c:'#1d4ed8'}, ongoing:{bg:'#fef3c7',c:'#92400e'}, marks_entry:{bg:'#fff7ed',c:'#c2410c'}, completed:{bg:'#f0fdf4',c:'#15803d'}, published:{bg:'#d1fae5',c:'#065f46'}, archived:{bg:'#f1f5f9',c:'#475569'} };
const RESULT   = { pass:{bg:'#d1fae5',c:'#065f46'}, fail:{bg:'#fee2e2',c:'#991b1b'}, promoted:{bg:'#eff6ff',c:'#1d4ed8'}, detained:{bg:'#fef3c7',c:'#92400e'}, withheld:{bg:'#f1f5f9',c:'#475569'} };
const RC_STA   = { draft:{bg:'#f1f5f9',c:'#475569'}, ready:{bg:'#fef3c7',c:'#92400e'}, approved:{bg:'#eff6ff',c:'#1d4ed8'}, published:{bg:'#d1fae5',c:'#065f46'}, archived:{bg:'#f1f5f9',c:'#475569'} };

const Chip = ({ map, val }) => {
  const c = map[val] || { bg: '#f1f5f9', c: '#475569' };
  return <span style={{ background: c.bg, color: c.c, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{(val || '').replace(/_/g, ' ')}</span>;
};

/* ── blank form objects ───────────────────────────────────────────── */
const BLANK_EXAM = { name: '', exam_type: 'unit_test', class_id: '', class_name: '', academic_year_id: '', academic_year: '', term: '', total_marks: 100, pass_marks: 35, start_date: '', end_date: '', result_declaration_date: '', instructions: '', status: 'scheduled' };
const BLANK_SUBJ = { exam: '', subject_id: '', subject_name: '', subject_code: '', exam_date: '', exam_time: '', duration_minutes: 180, theory_marks: 80, practical_marks: 20, total_marks: 100, pass_marks: 35, teacher_id: '', teacher_name: '' };
const BLANK_MARKS = { exam: '', exam_subject: '', student_id: '', student_name: '', admission_number: '', roll_number: '', theory_marks_obtained: '', practical_marks_obtained: '', is_absent: false, remarks: '' };
const BLANK_SCALE = { grading_system: '', name: '', grade: '', min_percent: '', max_percent: '', grade_points: '', is_pass: true, color: '#10b981', description: '' };
const BLANK_SYSTEM = { name: '', system_type: 'percentage', description: '', max_gpa: 10, is_default: false };

export default function GradeManagement() {
  const [tab, setTab] = useState('exams');
  const [exams, setExams] = useState([]);
  const [examSubjects, setExamSubjects] = useState([]);
  const [marks, setMarks] = useState([]);
  const [reportCards, setReportCards] = useState([]);
  const [gradingSystems, setGradingSystems] = useState([]);
  const [gradeScales, setGradeScales] = useState([]);
  const [subjectSummaries, setSubjectSummaries] = useState([]);
  const [examStats, setExamStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /* filters */
  const [examStaF, setExamStaF] = useState('');
  const [examTypeF, setExamTypeF] = useState('');
  const [selectedExam, setSelectedExam] = useState(null);
  const [rcStaF, setRcStaF] = useState('');
  const [search, setSearch] = useState('');

  /* modals */
  const [showExamForm, setShowExamForm] = useState(false);
  const [showSubjForm, setShowSubjForm] = useState(false);
  const [showMarksForm, setShowMarksForm] = useState(false);
  const [showScaleForm, setShowScaleForm] = useState(false);
  const [showSystemForm, setShowSystemForm] = useState(false);
  const [editExam, setEditExam] = useState(null);
  const [editScale, setEditScale] = useState(null);

  /* form state */
  const [examForm, setExamForm] = useState(BLANK_EXAM);
  const [subjForm, setSubjForm] = useState(BLANK_SUBJ);
  const [marksForm, setMarksForm] = useState(BLANK_MARKS);
  const [scaleForm, setScaleForm] = useState(BLANK_SCALE);
  const [systemForm, setSystemForm] = useState(BLANK_SYSTEM);

  const EF = (k, v) => setExamForm(f => ({ ...f, [k]: v }));
  const SF = (k, v) => setSubjForm(f => ({ ...f, [k]: v }));
  const MF = (k, v) => setMarksForm(f => ({ ...f, [k]: v }));
  const KF = (k, v) => setScaleForm(f => ({ ...f, [k]: v }));

  const load = useCallback(() => {
    setLoading(true);
    const ep = {}; if (examStaF) ep.status = examStaF; if (examTypeF) ep.exam_type = examTypeF;
    const rp = {}; if (rcStaF) rp.status = rcStaF; if (search) rp.search = search;
    Promise.all([
      gradesAPI.exams(ep), gradesAPI.examStats(), gradesAPI.reportCards(rp),
      gradesAPI.systems(), gradesAPI.scales({}),
    ]).then(([ex, st, rc, gs, gsc]) => {
      setExams(ex.data.results || ex.data);
      setExamStats(st.data || {});
      setReportCards(rc.data.results || rc.data);
      setGradingSystems(gs.data.results || gs.data);
      setGradeScales(gsc.data.results || gsc.data);
    }).catch(() => toast.error('Failed to load grade data')).finally(() => setLoading(false));
  }, [examStaF, examTypeF, rcStaF, search]);

  useEffect(() => { load(); }, [load]);

  /* load subjects when exam selected */
  useEffect(() => {
    if (selectedExam) {
      gradesAPI.examSubjects({ exam: selectedExam.id }).then(r => setExamSubjects(r.data.results || r.data));
      gradesAPI.marks({ exam: selectedExam.id, page_size: 200 }).then(r => setMarks(r.data.results || r.data));
      gradesAPI.subjectSummaries({ exam: selectedExam.id }).then(r => setSubjectSummaries(r.data.results || r.data));
    }
  }, [selectedExam]);

  /* ─── save handlers ─────────────────────────────────────────────── */
  const saveExam = async e => {
    e.preventDefault(); setSaving(true);
    try {
      editExam ? await gradesAPI.updateExam(editExam.id, examForm) : await gradesAPI.createExam(examForm);
      toast.success(editExam ? 'Exam updated!' : 'Exam created!');
      setShowExamForm(false); setEditExam(null); setExamForm(BLANK_EXAM); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const saveSubject = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await gradesAPI.createExamSubject({ ...subjForm, exam: selectedExam?.id });
      toast.success('Subject added!'); setShowSubjForm(false); setSubjForm(BLANK_SUBJ);
      if (selectedExam) gradesAPI.examSubjects({ exam: selectedExam.id }).then(r => setExamSubjects(r.data.results || r.data));
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const saveMarks = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await gradesAPI.createMarks({ ...marksForm, exam: selectedExam?.id });
      toast.success('Marks saved!'); setShowMarksForm(false); setMarksForm(BLANK_MARKS);
      if (selectedExam) gradesAPI.marks({ exam: selectedExam.id, page_size: 200 }).then(r => setMarks(r.data.results || r.data));
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const saveScale = async e => {
    e.preventDefault(); setSaving(true);
    try {
      editScale ? await gradesAPI.updateScale(editScale.id, scaleForm) : await gradesAPI.createScale(scaleForm);
      toast.success(editScale ? 'Scale updated!' : 'Grade scale added!');
      setShowScaleForm(false); setEditScale(null); setScaleForm(BLANK_SCALE); load();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const publishExam = async ex => {
    if (!window.confirm(`Publish results for "${ex.name}"? This will auto-generate report cards.`)) return;
    try {
      const r = await gradesAPI.publishResults(ex.id);
      toast.success(`Published! ${r.data.report_cards} report cards generated.`); load();
    } catch { toast.error('Failed to publish'); }
  };

  const openEditExam = ex => {
    setEditExam(ex);
    setExamForm({ name: ex.name, exam_type: ex.exam_type, class_id: ex.class_id, class_name: ex.class_name, academic_year_id: ex.academic_year_id, academic_year: ex.academic_year, term: ex.term, total_marks: ex.total_marks, pass_marks: ex.pass_marks, start_date: ex.start_date, end_date: ex.end_date, result_declaration_date: ex.result_declaration_date || '', instructions: ex.instructions || '', status: ex.status });
    setShowExamForm(true);
  };

  const statCards = [
    { icon: '📝', label: 'Total Exams', value: examStats.total_exams || exams.length, color: 'blue' },
    { icon: '📅', label: 'Scheduled', value: examStats.scheduled || exams.filter(e => e.status === 'scheduled').length, color: 'orange' },
    { icon: '✏️', label: 'Marks Entry Open', value: examStats.marks_entry || exams.filter(e => e.status === 'marks_entry').length, color: 'yellow' },
    { icon: '✅', label: 'Published', value: examStats.published || exams.filter(e => e.status === 'published').length, color: 'green' },
    { icon: '📊', label: 'Report Cards', value: examStats.report_cards_generated || reportCards.length, color: 'purple' },
    { icon: '📤', label: 'Published Cards', value: examStats.report_cards_published || reportCards.filter(r => r.status === 'published').length, color: 'teal' },
  ];

  const TABS = [
    { key: 'exams', label: '📝 Exams', count: exams.length },
    { key: 'marks_entry', label: '✏️ Marks Entry', count: marks.length },
    { key: 'report_cards', label: '📊 Report Cards', count: reportCards.length },
    { key: 'grading', label: '🏆 Grading Scales', count: gradeScales.length },
    { key: 'analytics', label: '📈 Analytics', count: null },
  ];

  return (
    <div>
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="page-header">
        <div>
          <h2 style={{ margin: 0, fontWeight: 800 }}>🏆 Grade Management</h2>
          <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>Exam scheduling, marks entry, grading, report cards & performance analytics</p>
        </div>
        <div className="d-flex gap-2">
          {tab === 'exams' && <button className="btn btn-primary" onClick={() => { setEditExam(null); setExamForm(BLANK_EXAM); setShowExamForm(true); }}>➕ Create Exam</button>}
          {tab === 'marks_entry' && selectedExam && (
            <div className="d-flex gap-2">
              <button className="btn btn-outline-primary" onClick={() => setShowSubjForm(true)}>📚 Add Subject</button>
              <button className="btn btn-primary" onClick={() => setShowMarksForm(true)}>✏️ Enter Marks</button>
            </div>
          )}
          {tab === 'grading' && (
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary" onClick={() => setShowSystemForm(true)}>⚙️ Grading System</button>
              <button className="btn btn-primary" onClick={() => { setEditScale(null); setScaleForm(BLANK_SCALE); setShowScaleForm(true); }}>➕ Add Grade Scale</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Stats ──────────────────────────────────────────────────── */}
      <div className="row g-3 mb-4">
        {statCards.map(s => (
          <div key={s.label} className="col-6 col-md-4 col-lg-2">
            <StatCard icon={s.icon} label={s.label} value={s.value} color={s.color} />
          </div>
        ))}
      </div>

      {/* ── Tab bar ────────────────────────────────────────────────── */}
      <div className="d-flex gap-2 mb-4 flex-wrap">
        {TABS.map(t => (
          <button key={t.key} className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab(t.key)}>
            {t.label}{t.count !== null && <span className="badge ms-1" style={{ background: tab === t.key ? 'rgba(255,255,255,0.3)' : '#e2e8f0', color: tab === t.key ? '#fff' : '#475569' }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════
          TAB: EXAMS
      ════════════════════════════════════════════════════════════ */}
      {tab === 'exams' && (
        <div>
          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-2">
                <div className="col-md-3">
                  <select className="form-select" value={examStaF} onChange={e => setExamStaF(e.target.value)}>
                    <option value="">All Status</option>
                    {Object.keys(EXAM_STA).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <select className="form-select" value={examTypeF} onChange={e => setExamTypeF(e.target.value)}>
                    <option value="">All Types</option>
                    {['unit_test', 'midterm', 'final', 'quarterly', 'half_yearly', 'annual', 'practice', 'competitive'].map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div className="col-md-2"><button className="btn btn-outline-secondary w-100" onClick={() => { setExamStaF(''); setExamTypeF(''); }}>🔄 Reset</button></div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header"><span>Examinations ({exams.length})</span></div>
            {loading ? <div className="loading-spinner"><div className="spinner-border text-primary" /></div> :
              !exams.length ? <div className="empty-state"><div className="empty-icon">📝</div><h5>No exams scheduled</h5><p>Create your first examination to begin.</p></div> : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead style={{ background: '#f8fafc', fontSize: 12, fontWeight: 700, color: '#475569' }}>
                      <tr><th>Exam Name</th><th>Class</th><th>Term</th><th>Type</th><th>Dates</th><th>Total Marks</th><th>Pass Marks</th><th>Students</th><th>Status</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {exams.map(ex => (
                        <tr key={ex.id}>
                          <td>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{ex.name}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{ex.academic_year}</div>
                          </td>
                          <td style={{ fontSize: 12, fontWeight: 600 }}>{ex.class_name}</td>
                          <td style={{ fontSize: 12 }}>{ex.term}</td>
                          <td><span className="badge bg-light text-dark" style={{ fontSize: 11 }}>{ex.exam_type.replace(/_/g, ' ')}</span></td>
                          <td style={{ fontSize: 12 }}>{formatDate(ex.start_date)} → {formatDate(ex.end_date)}</td>
                          <td style={{ textAlign: 'center', fontWeight: 700 }}>{ex.total_marks}</td>
                          <td style={{ textAlign: 'center' }}>{ex.pass_marks}</td>
                          <td style={{ textAlign: 'center' }}>{ex.students_count || '—'}</td>
                          <td><Chip map={EXAM_STA} val={ex.status} /></td>
                          <td>
                            <div className="d-flex gap-1 flex-wrap">
                              <button className="btn btn-sm btn-outline-secondary" title="Marks Entry" onClick={() => { setSelectedExam(ex); setTab('marks_entry'); }}>✏️</button>
                              <button className="btn btn-sm btn-outline-primary" onClick={() => openEditExam(ex)}>🖊️</button>
                              {['completed', 'marks_entry'].includes(ex.status) && (
                                <button className="btn btn-sm btn-outline-success" onClick={() => publishExam(ex)}>📤 Publish</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════
          TAB: MARKS ENTRY
      ════════════════════════════════════════════════════════════ */}
      {tab === 'marks_entry' && (
        <div>
          {/* Exam selector */}
          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-2 align-items-center">
                <div className="col-md-5">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 12 }}>Select Examination</label>
                  <select className="form-select" value={selectedExam?.id || ''} onChange={e => setSelectedExam(exams.find(ex => ex.id === +e.target.value) || null)}>
                    <option value="">— Choose Exam —</option>
                    {exams.map(ex => <option key={ex.id} value={ex.id}>{ex.name} ({ex.class_name} · {ex.academic_year})</option>)}
                  </select>
                </div>
                {selectedExam && (
                  <div className="col-auto d-flex gap-2 mt-auto">
                    <Chip map={EXAM_STA} val={selectedExam.status} />
                    <span style={{ fontSize: 12, color: '#64748b' }}>Total: {selectedExam.total_marks} · Pass: {selectedExam.pass_marks}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {selectedExam && (
            <div className="row g-3">
              {/* Subjects */}
              <div className="col-md-4">
                <div className="card">
                  <div className="card-header"><span>📚 Exam Subjects ({examSubjects.length})</span><button className="btn btn-sm btn-primary" onClick={() => { setSubjForm({ ...BLANK_SUBJ, exam: selectedExam.id }); setShowSubjForm(true); }}>+</button></div>
                  <div className="list-group list-group-flush">
                    {examSubjects.map(s => (
                      <div key={s.id} className="list-group-item px-3 py-2">
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{s.subject_name}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          📅 {s.exam_date ? formatDate(s.exam_date) : 'TBD'} · ⏱️ {s.duration_minutes}min
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>
                          Theory: {s.theory_marks} · Practical: {s.practical_marks} · Total: {s.total_marks} · Pass: {s.pass_marks}
                        </div>
                        {s.teacher_name && <div style={{ fontSize: 11, color: '#94a3b8' }}>👨‍🏫 {s.teacher_name}</div>}
                      </div>
                    ))}
                    {!examSubjects.length && <div className="list-group-item text-muted text-center py-4" style={{ fontSize: 13 }}>No subjects added yet</div>}
                  </div>
                </div>
              </div>

              {/* Marks table */}
              <div className="col-md-8">
                <div className="card">
                  <div className="card-header"><span>✏️ Marks Entry ({marks.length} entries)</span><button className="btn btn-sm btn-primary" onClick={() => { setMarksForm({ ...BLANK_MARKS, exam: selectedExam.id }); setShowMarksForm(true); }}>+ Add Marks</button></div>
                  {!marks.length ? <div className="empty-state py-4"><div className="empty-icon" style={{ fontSize: 32 }}>✏️</div><p style={{ fontSize: 13 }}>No marks entered yet. Add subjects first, then enter marks.</p></div> : (
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead style={{ background: '#f8fafc', fontSize: 11, fontWeight: 700, color: '#475569' }}>
                          <tr><th>Student</th><th>Roll No.</th><th>Subject</th><th>Theory</th><th>Practical</th><th>Total</th><th>%</th><th>Grade</th><th>Result</th><th>Absent</th></tr>
                        </thead>
                        <tbody>
                          {marks.map(m => (
                            <tr key={m.id} style={{ background: m.is_absent ? '#fff5f5' : !m.is_pass && m.is_pass !== null ? '#fff8f8' : 'transparent' }}>
                              <td style={{ fontWeight: 600, fontSize: 12 }}>{m.student_name}</td>
                              <td style={{ fontSize: 12 }}>{m.roll_number || m.admission_number}</td>
                              <td style={{ fontSize: 12 }}>{m.subject_name}</td>
                              <td style={{ fontSize: 12, textAlign: 'center' }}>{m.is_absent ? '—' : m.theory_marks_obtained ?? '—'}</td>
                              <td style={{ fontSize: 12, textAlign: 'center' }}>{m.is_absent ? '—' : m.practical_marks_obtained ?? '—'}</td>
                              <td style={{ fontSize: 13, fontWeight: 700, textAlign: 'center' }}>{m.is_absent ? 'AB' : m.total_marks_obtained ?? '—'}</td>
                              <td style={{ fontSize: 12, textAlign: 'center' }}>{m.percentage ? `${m.percentage}%` : '—'}</td>
                              <td style={{ textAlign: 'center' }}>
                                {m.grade && <span style={{ fontWeight: 800, color: m.grade_points >= 8 ? '#065f46' : m.grade_points >= 6 ? '#92400e' : '#991b1b', fontSize: 13 }}>{m.grade}</span>}
                              </td>
                              <td style={{ textAlign: 'center' }}>{m.is_pass === null ? '—' : m.is_pass ? <span style={{ color: '#065f46', fontWeight: 700, fontSize: 12 }}>PASS</span> : <span style={{ color: '#991b1b', fontWeight: 700, fontSize: 12 }}>FAIL</span>}</td>
                              <td style={{ textAlign: 'center' }}>{m.is_absent && <span style={{ color: '#991b1b', fontSize: 12, fontWeight: 700 }}>AB</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {!selectedExam && (
            <div className="empty-state">
              <div className="empty-icon">📝</div>
              <h5>Select an exam to enter marks</h5>
              <p>Choose an examination from the dropdown above to view subjects and enter student marks.</p>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════
          TAB: REPORT CARDS
      ════════════════════════════════════════════════════════════ */}
      {tab === 'report_cards' && (
        <div>
          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-2">
                <div className="col-md-4"><SearchBar onSearch={setSearch} placeholder="Search student name, admission no…" /></div>
                <div className="col-md-2">
                  <select className="form-select" value={rcStaF} onChange={e => setRcStaF(e.target.value)}>
                    <option value="">All Status</option>
                    {Object.keys(RC_STA).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-md-2"><button className="btn btn-outline-secondary w-100" onClick={() => { setSearch(''); setRcStaF(''); }}>🔄</button></div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header"><span>📊 Report Cards ({reportCards.length})</span></div>
            {!reportCards.length ? <div className="empty-state"><div className="empty-icon">📊</div><h5>No report cards generated</h5><p>Publish exam results to auto-generate report cards.</p></div> : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead style={{ background: '#f8fafc', fontSize: 12, fontWeight: 700, color: '#475569' }}>
                    <tr><th>Student</th><th>Exam</th><th>Class</th><th>Total Marks</th><th>Percentage</th><th>Grade</th><th>Class Rank</th><th>Result</th><th>Attendance%</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {reportCards.map(rc => (
                      <tr key={rc.id}>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{rc.student_name}</div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>Adm: {rc.admission_number}{rc.roll_number && ` · Roll: ${rc.roll_number}`}</div>
                        </td>
                        <td style={{ fontSize: 12 }}>{rc.exam_name}</td>
                        <td style={{ fontSize: 12 }}>{rc.class_name}{rc.section_name && ` – ${rc.section_name}`}</td>
                        <td style={{ textAlign: 'center', fontSize: 12 }}>{rc.total_marks_obtained}/{rc.total_max_marks}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span style={{ fontWeight: 800, fontSize: 14, color: rc.percentage >= 75 ? '#065f46' : rc.percentage >= 50 ? '#92400e' : '#991b1b' }}>{Number(rc.percentage).toFixed(1)}%</span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {rc.overall_grade && <span style={{ fontWeight: 800, fontSize: 15, color: '#1d4ed8' }}>{rc.overall_grade}</span>}
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 700 }}>{rc.class_rank ? `#${rc.class_rank}` : '—'}{rc.total_students ? <span style={{ fontSize: 11, color: '#94a3b8' }}> /{rc.total_students}</span> : ''}</td>
                        <td>{rc.result && <Chip map={RESULT} val={rc.result} />}</td>
                        <td style={{ textAlign: 'center', fontSize: 12 }}>{rc.attendance_percentage ? `${Number(rc.attendance_percentage).toFixed(1)}%` : '—'}</td>
                        <td><Chip map={RC_STA} val={rc.status} /></td>
                        <td>
                          <div className="d-flex gap-1">
                            {rc.status !== 'published' && (
                              <button className="btn btn-sm btn-outline-success" onClick={() => gradesAPI.publishReportCard(rc.id).then(() => { toast.success('Published to parents!'); load(); })}>📤</button>
                            )}
                            <button className="btn btn-sm btn-outline-primary" title="View / Print">🖨️</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════
          TAB: GRADING SCALES
      ════════════════════════════════════════════════════════════ */}
      {tab === 'grading' && (
        <div className="row g-3">
          {/* Systems */}
          <div className="col-md-4">
            <div className="card">
              <div className="card-header"><span>⚙️ Grading Systems ({gradingSystems.length})</span></div>
              <div className="list-group list-group-flush">
                {gradingSystems.map(gs => (
                  <div key={gs.id} className="list-group-item px-3 py-3">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{gs.name}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{gs.system_type} · Max GPA: {gs.max_gpa}</div>
                        {gs.is_default && <span style={{ fontSize: 10, background: '#d1fae5', color: '#065f46', padding: '1px 8px', borderRadius: 20, fontWeight: 700 }}>DEFAULT</span>}
                      </div>
                    </div>
                  </div>
                ))}
                {!gradingSystems.length && <div className="list-group-item text-muted text-center py-3" style={{ fontSize: 12 }}>No grading systems defined</div>}
              </div>
            </div>
          </div>

          {/* Grade Scales */}
          <div className="col-md-8">
            <div className="card">
              <div className="card-header"><span>🏆 Grade Scales ({gradeScales.length})</span></div>
              {!gradeScales.length ? <div className="empty-state"><div className="empty-icon">🏆</div><h5>No grade scales defined</h5></div> : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead style={{ background: '#f8fafc', fontSize: 12, fontWeight: 700, color: '#475569' }}>
                      <tr><th>Grade</th><th>Name / Description</th><th>Min %</th><th>Max %</th><th>Grade Points</th><th>Pass/Fail</th><th>Colour</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {gradeScales.map(gs => (
                        <tr key={gs.id}>
                          <td><span style={{ fontWeight: 900, fontSize: 20, color: gs.color }}>{gs.grade}</span></td>
                          <td>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{gs.name}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{gs.description}</div>
                          </td>
                          <td style={{ textAlign: 'center', fontWeight: 700 }}>{gs.min_percent}%</td>
                          <td style={{ textAlign: 'center', fontWeight: 700 }}>{gs.max_percent}%</td>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: '#1d4ed8' }}>{gs.grade_points}</td>
                          <td style={{ textAlign: 'center' }}>{gs.is_pass ? <span style={{ color: '#065f46', fontWeight: 700 }}>✅ Pass</span> : <span style={{ color: '#991b1b', fontWeight: 700 }}>❌ Fail</span>}</td>
                          <td>
                            <div style={{ width: 28, height: 28, borderRadius: 8, background: gs.color, display: 'inline-block' }} />
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <button className="btn btn-sm btn-outline-primary" onClick={() => { setEditScale(gs); setScaleForm({ grading_system: gs.grading_system || '', name: gs.name, grade: gs.grade, min_percent: gs.min_percent, max_percent: gs.max_percent, grade_points: gs.grade_points, is_pass: gs.is_pass, color: gs.color, description: gs.description || '' }); setShowScaleForm(true); }}>✏️</button>
                              <button className="btn btn-sm btn-outline-danger" onClick={() => { if (window.confirm('Delete grade scale?')) gradesAPI.deleteScale(gs.id).then(() => { toast.success('Deleted'); load(); }); }}>🗑️</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════
          TAB: ANALYTICS
      ════════════════════════════════════════════════════════════ */}
      {tab === 'analytics' && (
        <div className="row g-3">
          <div className="col-md-4">
            <div className="card mb-3">
              <div className="card-header"><strong>📊 Select Exam for Analysis</strong></div>
              <div className="card-body">
                <select className="form-select mb-3" value={selectedExam?.id || ''} onChange={e => setSelectedExam(exams.find(ex => ex.id === +e.target.value) || null)}>
                  <option value="">— Select Exam —</option>
                  {exams.filter(ex => ex.status === 'published').map(ex => <option key={ex.id} value={ex.id}>{ex.name} ({ex.class_name})</option>)}
                </select>
                {selectedExam && (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 8 }}>Exam Info</div>
                    {[['Class', selectedExam.class_name], ['Type', selectedExam.exam_type], ['Term', selectedExam.term], ['Status', selectedExam.status]].map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <span style={{ color: '#94a3b8' }}>{k}</span><span style={{ fontWeight: 600 }}>{v}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-8">
            {selectedExam ? (
              <div>
                {/* Subject-wise performance */}
                <div className="card mb-3">
                  <div className="card-header"><strong>📚 Subject-wise Performance</strong></div>
                  {!subjectSummaries.length ? <div className="card-body text-muted text-center py-3" style={{ fontSize: 13 }}>No analytics available. Publish results first.</div> : (
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead style={{ background: '#f8fafc', fontSize: 11, fontWeight: 700, color: '#475569' }}>
                          <tr><th>Subject</th><th>Appeared</th><th>Passed</th><th>Failed</th><th>Absent</th><th>Highest</th><th>Average</th><th>Pass%</th></tr>
                        </thead>
                        <tbody>
                          {subjectSummaries.map(s => (
                            <tr key={s.id}>
                              <td style={{ fontWeight: 600, fontSize: 12 }}>{s.subject_name}</td>
                              <td style={{ textAlign: 'center', fontSize: 12 }}>{s.appeared}</td>
                              <td style={{ textAlign: 'center', fontSize: 12, color: '#065f46', fontWeight: 700 }}>{s.passed}</td>
                              <td style={{ textAlign: 'center', fontSize: 12, color: '#991b1b', fontWeight: 700 }}>{s.failed}</td>
                              <td style={{ textAlign: 'center', fontSize: 12, color: '#92400e' }}>{s.absent}</td>
                              <td style={{ textAlign: 'center', fontSize: 12 }}>{s.highest_marks}</td>
                              <td style={{ textAlign: 'center', fontSize: 12 }}>{Number(s.average_marks).toFixed(1)}</td>
                              <td style={{ textAlign: 'center' }}>
                                <span style={{ fontWeight: 700, color: s.pass_percentage >= 75 ? '#065f46' : s.pass_percentage >= 50 ? '#92400e' : '#991b1b', fontSize: 13 }}>{Number(s.pass_percentage).toFixed(1)}%</span>
                                <div style={{ height: 4, background: '#f1f5f9', borderRadius: 2, marginTop: 3, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${s.pass_percentage}%`, background: s.pass_percentage >= 75 ? '#10b981' : s.pass_percentage >= 50 ? '#f59e0b' : '#ef4444', borderRadius: 2, transition: 'width .5s' }} />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                {/* Top performers */}
                <div className="card">
                  <div className="card-header"><strong>🏆 Class Toppers</strong></div>
                  <div className="list-group list-group-flush">
                    {reportCards.filter(rc => rc.exam_name === selectedExam.name).sort((a, b) => b.percentage - a.percentage).slice(0, 10).map((rc, i) => (
                      <div key={rc.id} className="list-group-item px-3 py-2 d-flex align-items-center gap-3">
                        <div style={{ width: 32, height: 32, borderRadius: 10, background: i === 0 ? '#fef3c7' : i === 1 ? '#f1f5f9' : i === 2 ? '#fff7ed' : '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, color: i === 0 ? '#92400e' : '#64748b' }}>
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{rc.student_name}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{rc.admission_number}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 800, fontSize: 16, color: '#1d4ed8' }}>{Number(rc.percentage).toFixed(1)}%</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{rc.total_marks_obtained}/{rc.total_max_marks}</div>
                        </div>
                      </div>
                    ))}
                    {!reportCards.filter(rc => rc.exam_name === selectedExam.name).length && <div className="list-group-item text-muted text-center py-3" style={{ fontSize: 13 }}>Publish results to see toppers</div>}
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">📈</div>
                <h5>Select a published exam</h5>
                <p>Choose an exam from the left panel to view performance analytics.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════ MODALS ══════════════════════════════════ */}

      {/* Exam Form Modal */}
      {showExamForm && (
        <Modal title={editExam ? `✏️ Edit Exam — ${editExam.name}` : '➕ Create Examination'} onClose={() => setShowExamForm(false)} size="xl">
          <form onSubmit={saveExam}>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-6"><label className="form-label fw-semibold">Exam Name *</label><input className="form-control" value={examForm.name} onChange={e => EF('name', e.target.value)} required placeholder="e.g. Unit Test 1 – Term 1 2024" /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Exam Type *</label>
                  <select className="form-select" value={examForm.exam_type} onChange={e => EF('exam_type', e.target.value)}>
                    {['unit_test', 'midterm', 'final', 'quarterly', 'half_yearly', 'annual', 'practice', 'competitive'].map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div className="col-md-3"><label className="form-label fw-semibold">Status</label>
                  <select className="form-select" value={examForm.status} onChange={e => EF('status', e.target.value)}>
                    {Object.keys(EXAM_STA).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div className="col-md-3"><label className="form-label fw-semibold">Class Name *</label><input className="form-control" value={examForm.class_name} onChange={e => EF('class_name', e.target.value)} required placeholder="e.g. Class 10" /></div>
                <div className="col-md-2"><label className="form-label fw-semibold">Class ID *</label><input type="number" className="form-control" value={examForm.class_id} onChange={e => EF('class_id', e.target.value)} required /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Academic Year *</label><input className="form-control" value={examForm.academic_year} onChange={e => EF('academic_year', e.target.value)} required placeholder="e.g. 2024-25" /></div>
                <div className="col-md-4"><label className="form-label fw-semibold">Term *</label><input className="form-control" value={examForm.term} onChange={e => EF('term', e.target.value)} required placeholder="e.g. Term 1, Semester 2" /></div>
                <div className="col-md-2"><label className="form-label fw-semibold">Total Marks</label><input type="number" className="form-control" value={examForm.total_marks} onChange={e => EF('total_marks', +e.target.value)} min={1} /></div>
                <div className="col-md-2"><label className="form-label fw-semibold">Pass Marks</label><input type="number" className="form-control" value={examForm.pass_marks} onChange={e => EF('pass_marks', +e.target.value)} min={0} /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Start Date *</label><input type="date" className="form-control" value={examForm.start_date} onChange={e => EF('start_date', e.target.value)} required /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">End Date *</label><input type="date" className="form-control" value={examForm.end_date} onChange={e => EF('end_date', e.target.value)} required /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Result Declaration Date</label><input type="date" className="form-control" value={examForm.result_declaration_date} onChange={e => EF('result_declaration_date', e.target.value)} /></div>
                <div className="col-12"><label className="form-label fw-semibold">Instructions / Guidelines</label><textarea className="form-control" rows={3} value={examForm.instructions} onChange={e => EF('instructions', e.target.value)} placeholder="Exam instructions for students…" /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowExamForm(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : editExam ? '💾 Update Exam' : '📝 Create Exam'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Subject Form Modal */}
      {showSubjForm && (
        <Modal title={`📚 Add Subject — ${selectedExam?.name}`} onClose={() => setShowSubjForm(false)} size="lg">
          <form onSubmit={saveSubject}>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-5"><label className="form-label fw-semibold">Subject Name *</label><input className="form-control" value={subjForm.subject_name} onChange={e => SF('subject_name', e.target.value)} required /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Subject Code</label><input className="form-control" value={subjForm.subject_code} onChange={e => SF('subject_code', e.target.value)} placeholder="e.g. MATH" /></div>
                <div className="col-md-4"><label className="form-label fw-semibold">Subject ID</label><input type="number" className="form-control" value={subjForm.subject_id} onChange={e => SF('subject_id', e.target.value)} /></div>
                <div className="col-md-4"><label className="form-label fw-semibold">Exam Date</label><input type="date" className="form-control" value={subjForm.exam_date} onChange={e => SF('exam_date', e.target.value)} /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Exam Time</label><input type="time" className="form-control" value={subjForm.exam_time} onChange={e => SF('exam_time', e.target.value)} /></div>
                <div className="col-md-2"><label className="form-label fw-semibold">Duration (min)</label><input type="number" className="form-control" value={subjForm.duration_minutes} onChange={e => SF('duration_minutes', +e.target.value)} /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Theory Marks</label><input type="number" className="form-control" value={subjForm.theory_marks} onChange={e => SF('theory_marks', +e.target.value)} /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Practical Marks</label><input type="number" className="form-control" value={subjForm.practical_marks} onChange={e => SF('practical_marks', +e.target.value)} /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Total Marks</label><input type="number" className="form-control" value={subjForm.total_marks} onChange={e => SF('total_marks', +e.target.value)} /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Pass Marks</label><input type="number" className="form-control" value={subjForm.pass_marks} onChange={e => SF('pass_marks', +e.target.value)} /></div>
                <div className="col-md-5"><label className="form-label fw-semibold">Teacher Name</label><input className="form-control" value={subjForm.teacher_name} onChange={e => SF('teacher_name', e.target.value)} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowSubjForm(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : '📚 Add Subject'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Marks Entry Form */}
      {showMarksForm && (
        <Modal title={`✏️ Enter Marks — ${selectedExam?.name}`} onClose={() => setShowMarksForm(false)} size="lg">
          <form onSubmit={saveMarks}>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-6"><label className="form-label fw-semibold">Subject *</label>
                  <select className="form-select" value={marksForm.exam_subject} onChange={e => MF('exam_subject', e.target.value)} required>
                    <option value="">Select Subject</option>
                    {examSubjects.map(s => <option key={s.id} value={s.id}>{s.subject_name} (Max: {s.total_marks})</option>)}
                  </select>
                </div>
                <div className="col-md-3"><label className="form-label fw-semibold">Student ID *</label><input type="number" className="form-control" value={marksForm.student_id} onChange={e => MF('student_id', e.target.value)} required /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Roll Number</label><input className="form-control" value={marksForm.roll_number} onChange={e => MF('roll_number', e.target.value)} /></div>
                <div className="col-12"><label className="form-label fw-semibold">Student Name *</label><input className="form-control" value={marksForm.student_name} onChange={e => MF('student_name', e.target.value)} required /></div>
                <div className="col-md-4"><label className="form-label fw-semibold">Admission Number</label><input className="form-control" value={marksForm.admission_number} onChange={e => MF('admission_number', e.target.value)} /></div>
                <div className="col-md-4"><label className="form-label fw-semibold">Theory Marks</label><input type="number" className="form-control" value={marksForm.theory_marks_obtained} onChange={e => MF('theory_marks_obtained', e.target.value)} min={0} step="0.5" disabled={marksForm.is_absent} /></div>
                <div className="col-md-4"><label className="form-label fw-semibold">Practical Marks</label><input type="number" className="form-control" value={marksForm.practical_marks_obtained} onChange={e => MF('practical_marks_obtained', e.target.value)} min={0} step="0.5" disabled={marksForm.is_absent} /></div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Mark as Absent</label>
                  <div className="mt-2"><label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}><input type="checkbox" checked={marksForm.is_absent} onChange={e => MF('is_absent', e.target.checked)} /> Student was absent</label></div>
                </div>
                <div className="col-md-8"><label className="form-label fw-semibold">Remarks</label><input className="form-control" value={marksForm.remarks} onChange={e => MF('remarks', e.target.value)} placeholder="Optional teacher remarks…" /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowMarksForm(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : '✏️ Save Marks'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Grade Scale Form */}
      {showScaleForm && (
        <Modal title={editScale ? '✏️ Edit Grade Scale' : '➕ Add Grade Scale'} onClose={() => setShowScaleForm(false)} size="lg">
          <form onSubmit={saveScale}>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-4"><label className="form-label fw-semibold">Grading System</label>
                  <select className="form-select" value={scaleForm.grading_system} onChange={e => KF('grading_system', e.target.value)}>
                    <option value="">None (Standalone)</option>
                    {gradingSystems.map(gs => <option key={gs.id} value={gs.id}>{gs.name}</option>)}
                  </select>
                </div>
                <div className="col-md-4"><label className="form-label fw-semibold">Grade Name *</label><input className="form-control" value={scaleForm.name} onChange={e => KF('name', e.target.value)} required placeholder="e.g. Outstanding, Excellent" /></div>
                <div className="col-md-2"><label className="form-label fw-semibold">Grade Letter *</label><input className="form-control" value={scaleForm.grade} onChange={e => KF('grade', e.target.value)} required placeholder="A+" style={{ fontWeight: 800, fontSize: 18 }} /></div>
                <div className="col-md-2"><label className="form-label fw-semibold">Colour</label><input type="color" className="form-control form-control-color" style={{ height: 38 }} value={scaleForm.color} onChange={e => KF('color', e.target.value)} /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Min Percentage *</label><input type="number" className="form-control" value={scaleForm.min_percent} onChange={e => KF('min_percent', e.target.value)} required min={0} max={100} step="0.01" /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Max Percentage *</label><input type="number" className="form-control" value={scaleForm.max_percent} onChange={e => KF('max_percent', e.target.value)} required min={0} max={100} step="0.01" /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Grade Points</label><input type="number" className="form-control" value={scaleForm.grade_points} onChange={e => KF('grade_points', e.target.value)} min={0} max={10} step="0.1" /></div>
                <div className="col-md-3"><label className="form-label fw-semibold">Pass / Fail</label>
                  <div className="mt-2"><label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}><input type="checkbox" checked={scaleForm.is_pass} onChange={e => KF('is_pass', e.target.checked)} /> This grade is a PASS</label></div>
                </div>
                <div className="col-12"><label className="form-label fw-semibold">Description</label><input className="form-control" value={scaleForm.description} onChange={e => KF('description', e.target.value)} placeholder="e.g. Excellent performance, above 90%" /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowScaleForm(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : editScale ? '💾 Update Scale' : '🏆 Add Grade Scale'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
